import React, { useEffect, useState } from 'react';
import parametroSistemaService, { type ParametroSistema } from '../services/parametroSistemaService';
import { IntegracoesTab } from './IntegracoesTab';
import Toast from '../components/Toast';
import { UI_THEME } from '../ui/theme';
import { FIELD_CLASS } from '../components/ui/fieldStyles';
import { Input } from '../components/ui/Field';
import Button from '../components/ui/Button';
import Tabs from '../components/ui/Tabs';
import { toUpperText } from '../utils/formatters';

const errorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { error?: unknown } } }).response;
    if (typeof response?.data?.error === 'string') return response.data.error;
  }
  return fallback;
};

type ToastState = { message: string; type: 'success' | 'error' };

const thClass = 'px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400';

const StatusBadge: React.FC<{ aberto: boolean; aberta: string; fechada: string }> = ({ aberto, aberta, fechada }) => (
  <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
    aberto
      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
      : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
  }`}>
    {aberto ? aberta : fechada}
  </span>
);

const PreProjetoPrazosTable: React.FC = () => {
  const [parametro, setParametro] = useState<ParametroSistema | null>(null);
  const [janelaInicio, setJanelaInicio] = useState('');
  const [janelaFim, setJanelaFim] = useState('');
  const [janelaFimSecretaria, setJanelaFimSecretaria] = useState('');
  const [retInicio, setRetInicio] = useState('');
  const [retFim, setRetFim] = useState('');
  const [cancInicio, setCancInicio] = useState('');
  const [cancFim, setCancFim] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<ToastState | null>(null);

  useEffect(() => {
    parametroSistemaService.getParametros()
      .then((data) => {
        setParametro(data);
        setJanelaInicio(data.preProjetoJanelaInicio ?? '');
        setJanelaFim(data.preProjetoJanelaFim ?? '');
        setJanelaFimSecretaria(data.preProjetoJanelaFimSecretaria ?? '');
        setRetInicio(data.preProjetoRetificacaoInicio ?? '');
        setRetFim(data.preProjetoRetificacaoFim ?? '');
        setCancInicio(data.preProjetoCancelamentoInicio ?? '');
        setCancFim(data.preProjetoCancelamentoFim ?? '');
      })
      .catch((err) => setToast({ message: errorMessage(err, 'Não foi possível carregar os parâmetros.'), type: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  const salvar = async () => {
    setSaving(true);
    try {
      await parametroSistemaService.atualizarJanelaPreProjeto(
        janelaInicio || null,
        janelaFim || null,
        janelaFimSecretaria || null,
      );
      await parametroSistemaService.atualizarJanelaRetificacaoPreProjeto(
        retInicio || null,
        retFim || null,
      );
      const atualizado = await parametroSistemaService.atualizarJanelaCancelamentoSelecao(
        cancInicio || null,
        cancFim || null,
      );
      setParametro(atualizado);
      setToast({ message: 'Prazos de pré-projeto atualizados.', type: 'success' });
    } catch (err) {
      setToast({ message: errorMessage(err, 'Não foi possível salvar.'), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="px-6 pb-3 pt-5">
        <h2 className="text-lg font-bold text-slate-950 dark:text-slate-100">Prazos de pré-projeto</h2>
        {parametro?.atualizadoPor && parametro?.atualizadoEm && (
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Última atualização por {parametro.atualizadoPor}</p>
        )}
        <ul className="mt-2 max-w-4xl space-y-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
          <li>
            <strong className="font-semibold">Janela de lançamento:</strong> período em que instituições não bloqueadas podem
            lançar pré-projeto para o próximo exercício. Data final opcional — sem ela, o aviso de período aberto segue
            exibido a partir da data inicial.
          </li>
          <li>
            <strong className="font-semibold">Retificação:</strong> período em que as instituições podem alterar o valor e a
            área de atuação de pré-projetos já publicados (e ainda não selecionados pelo parlamentar), informando o motivo,
            e também cadastrar e publicar novos pré-projetos. Sem data inicial, fica fechada.
          </li>
          <li>
            <strong className="font-semibold">Cancelar seleção:</strong> período em que o parlamentar pode cancelar a
            seleção de um pré-projeto, informando a justificativa. O pré-projeto volta a Publicado e a emenda gerada é
            apagada. Encerre o período antes da publicação da LOA — depois dele a seleção fica definitiva. Sem data inicial,
            fica fechado.
          </li>
          <li>
            <strong className="font-semibold">Data final extra:</strong> prorroga o prazo só para operadores de secretaria.
            Em branco, seguem a data final geral.
          </li>
        </ul>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[44rem] text-sm">
          <thead className="border-y border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-800/60">
            <tr>
              <th className={thClass}>Prazo</th>
              <th className={thClass}>Status</th>
              <th className={thClass}>Data inicial</th>
              <th className={thClass}>Data final</th>
              <th className={thClass}>
                Data final extra
                <br />
                <span className="normal-case tracking-normal">Operador de secretaria</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            <tr className="align-top">
              <td className="whitespace-nowrap px-4 py-3 font-semibold text-slate-900 dark:text-slate-100">
                Janela de lançamento
              </td>
              <td className="px-4 py-3">
                {!loading && <StatusBadge aberto={!!parametro?.preProjetoAberto} aberta="Aberto" fechada="Fechado" />}
              </td>
              <td className="px-4 py-3">
                <Input
                  type="date"
                  aria-label="Janela: data inicial"
                  value={janelaInicio}
                  disabled={loading}
                  onChange={(event) => setJanelaInicio(event.target.value)}
                  fieldWidth="sm"
                />
              </td>
              <td className="px-4 py-3">
                <Input
                  type="date"
                  aria-label="Janela: data final (opcional)"
                  value={janelaFim}
                  min={janelaInicio || undefined}
                  disabled={loading}
                  onChange={(event) => setJanelaFim(event.target.value)}
                  fieldWidth="sm"
                />
              </td>
              <td className="px-4 py-3">
                <Input
                  type="date"
                  aria-label="Janela: data final extra para operador de secretaria (opcional)"
                  value={janelaFimSecretaria}
                  min={janelaFim || janelaInicio || undefined}
                  disabled={loading}
                  onChange={(event) => setJanelaFimSecretaria(event.target.value)}
                  fieldWidth="sm"
                />
              </td>
            </tr>
            <tr className="align-top">
              <td className="whitespace-nowrap px-4 py-3 font-semibold text-slate-900 dark:text-slate-100">
                Retificação
              </td>
              <td className="px-4 py-3">
                {!loading && <StatusBadge aberto={!!parametro?.preProjetoRetificacaoAberta} aberta="Aberta" fechada="Fechada" />}
              </td>
              <td className="px-4 py-3">
                <Input
                  type="date"
                  aria-label="Retificação: data inicial"
                  value={retInicio}
                  disabled={loading}
                  onChange={(event) => setRetInicio(event.target.value)}
                  fieldWidth="sm"
                />
              </td>
              <td className="px-4 py-3">
                <Input
                  type="date"
                  aria-label="Retificação: data final"
                  value={retFim}
                  min={retInicio || undefined}
                  disabled={loading}
                  onChange={(event) => setRetFim(event.target.value)}
                  fieldWidth="sm"
                />
              </td>
              <td className="px-4 py-3 text-slate-400">—</td>
            </tr>
            <tr className="align-top">
              <td className="whitespace-nowrap px-4 py-3 font-semibold text-slate-900 dark:text-slate-100">
                Cancelar seleção
              </td>
              <td className="px-4 py-3">
                {!loading && <StatusBadge aberto={!!parametro?.preProjetoCancelamentoAberto} aberta="Aberto" fechada="Fechado" />}
              </td>
              <td className="px-4 py-3">
                <Input
                  type="date"
                  aria-label="Cancelar seleção: data inicial"
                  value={cancInicio}
                  disabled={loading}
                  onChange={(event) => setCancInicio(event.target.value)}
                  fieldWidth="sm"
                />
              </td>
              <td className="px-4 py-3">
                <Input
                  type="date"
                  aria-label="Cancelar seleção: data final"
                  value={cancFim}
                  min={cancInicio || undefined}
                  disabled={loading}
                  onChange={(event) => setCancFim(event.target.value)}
                  fieldWidth="sm"
                />
              </td>
              <td className="px-4 py-3 text-slate-400">—</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="flex justify-end border-t border-slate-100 px-6 py-3 dark:border-slate-800">
        <Button variant="primary" size="sm" onClick={() => void salvar()} disabled={saving || loading}>
          {saving ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </section>
  );
};

const ResponsavelTransparenciaCard: React.FC = () => {
  const [form, setForm] = useState({ unidadeResponsavel: '', contatoEmail: '', contatoTelefone: '', atoDesignacaoUrl: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    parametroSistemaService.getParametros()
      .then((data) => setForm({
        unidadeResponsavel: data.transparenciaUnidadeResponsavel ?? '',
        contatoEmail: data.transparenciaContatoEmail ?? '',
        contatoTelefone: data.transparenciaContatoTelefone ?? '',
        atoDesignacaoUrl: data.transparenciaAtoDesignacaoUrl ?? '',
      }))
      .catch((err) => setToast({ message: errorMessage(err, 'Não foi possível carregar os parâmetros.'), type: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  const salvar = async () => {
    setSaving(true);
    try {
      await parametroSistemaService.atualizarResponsavelTransparencia(form);
      setToast({ message: 'Responsável pela transparência atualizado.', type: 'success' });
    } catch (err) {
      setToast({ message: errorMessage(err, 'Não foi possível salvar.'), type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const inputClass = `${FIELD_CLASS} mt-1`;
  const campos: { key: keyof typeof form; label: string; type: string; placeholder?: string }[] = [
    { key: 'unidadeResponsavel', label: 'Unidade responsável', type: 'text', placeholder: 'Ex.: Controladoria-Geral do Município' },
    { key: 'atoDesignacaoUrl', label: 'Link do ato de designação', type: 'url', placeholder: 'https://...' },
    { key: 'contatoEmail', label: 'E-mail de contato', type: 'email' },
    { key: 'contatoTelefone', label: 'Telefone de contato', type: 'tel' },
  ];

  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <div className="p-6">
        <h2 className="text-lg font-bold text-slate-950 dark:text-slate-100">Portal da transparência — unidade responsável</h2>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
          Publicado no portal da transparência das emendas. Informe a unidade designada, o link do ato de designação
          e pelo menos um contato (e-mail ou telefone).
        </p>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          {campos.map(({ key, label, type, placeholder }) => (
            <label key={key} className="text-sm font-medium text-slate-700 dark:text-slate-200">
              {label}
              <input
                type={type}
                value={form[key]}
                placeholder={placeholder}
                onChange={(event) => setForm((prev) => ({
                  ...prev,
                  [key]: key === 'unidadeResponsavel' ? toUpperText(event.target.value) : event.target.value,
                }))}
                disabled={loading}
                className={inputClass}
              />
            </label>
          ))}
        </div>
        <div className="mt-4">
          <Button variant="primary" onClick={() => void salvar()} disabled={saving || loading}>
            {saving ? 'Salvando...' : 'Salvar'}
          </Button>
        </div>
      </div>
    </section>
  );
};

type TabKey = 'geral' | 'emendas' | 'sync';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'geral', label: 'Geral' },
  { key: 'emendas', label: 'Emendas' },
  { key: 'sync', label: 'Integrações' },
];

const ParametrosSistemaPage: React.FC = () => {
  const isSaas = UI_THEME === 'saas';
  const [activeTab, setActiveTab] = useState<TabKey>('geral');

  return (
    <div className="w-full space-y-6">
      <section className={`rounded-2xl border p-6 shadow-sm ${
        isSaas
          ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/50 to-indigo-50 dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
          : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900'
      }`}>
        <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-sky-700 dark:text-sky-400'}`}>
          Administrador
        </p>
        <h1 className="mt-2 text-3xl font-bold tracking-tight text-slate-950 dark:text-slate-100">Parâmetros do Sistema</h1>
        <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
          Configurações gerais do sistema, regras de emendas e integrações com sistemas externos.
        </p>
      </section>

      <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} ariaLabel="Seções dos parâmetros do sistema" />

      {activeTab === 'geral' && <ResponsavelTransparenciaCard />}

      {activeTab === 'emendas' && <PreProjetoPrazosTable />}

      {activeTab === 'sync' && <IntegracoesTab />}
    </div>
  );
};

export default ParametrosSistemaPage;
